// R3 composite + stream client for the embedded workspace.
//
// Mirrors services/api-server/routes/my-activity-regions.mjs. The
// screen (MyActivityReview) calls this via useMyActivityComposite
// below. One batched read fills every region; the SSE stream pushes
// incremental ticks for the live regions.

import { RuntimeApiError } from './runtimeClient.js';
import type { RuntimeApiErrorKind } from './types.js';
import { defaultFetch } from './default-fetch.js';

export type MyActivityRegionId =
  | 'activity-today'
  | 'daily-pulse'
  | 'earnings-card'
  | 'leaderboard-section'
  | 'conversion'
  | 'customers-helped'
  | 'retention'
  | 'response-times'
  | 'historical-view';

export const MY_ACTIVITY_REGIONS: readonly MyActivityRegionId[] = Object.freeze([
  'activity-today',
  'daily-pulse',
  'earnings-card',
  'leaderboard-section',
  'conversion',
  'customers-helped',
  'retention',
  'response-times',
  'historical-view',
]);

export type MyActivityScope = 'today' | 'week' | 'month' | 'historical';

export interface RegionEnvelope<T = unknown> {
  data: T | null;
  source: string;
  lastUpdated: number;
  degraded?: {
    reason: string;
    note: string;
  };
}

export interface ActivityTodayRegion {
  touches: { total: number; byType: { call: number; text: number; email: number; note: number } };
  workday: {
    state: 'not_clocked_in' | 'clocked_in' | 'on_break';
    sessionStartMs: number | null;
    activeBreak: { startTs: number; reason: string } | null;
    stale: boolean;
    lastTouchAtMs: number | null;
    currentCustomerId: string | null;
  };
  letters: { total: number; drafted: number; sent: number };
  coaching: { text: string | null; patternId: string | null } | null;
}

export interface ConversionRegion {
  scope: MyActivityScope;
  ratio: number | null;
  contactsWorked: number | null;
  closes: number | null;
  sevenDayAverage: number | null;
}

export interface CustomersHelpedRegion {
  scope: MyActivityScope;
  count: number;
  firstPage: Array<{ customerId: string; label?: string }>;
  source?: string;
}

export interface RetentionRegion {
  scope: MyActivityScope;
  confirmed: number | null;
  pending: number | null;
  notRetained: number | null;
  atRisk: number | null;
  topDrivers: Array<{ reason: string; count: number }>;
}

export interface ResponseTimesRegion {
  scope: MyActivityScope;
  calls: number | null;
  chat: number | null;
  text: number | null;
  email: number | null;
  counts: { calls: number; chat: number; text: number; email: number };
  avg: Record<string, number> | null;
  p50: Record<string, number> | null;
  p95: Record<string, number> | null;
}

export interface DailyPulseRegion {
  repId?: string;
  touches?: {
    total: number | null;
    byType?: { call?: number; text?: number; email?: number; note?: number } | null;
    degraded?: string | null;
  } | null;
  lettersToday?: {
    ready: number | null;
    needs_attention: number | null;
    mailed: number | null;
    degraded?: string | null;
  } | null;
  calls?: {
    inbound: number | null;
    outbound: number | null;
    missed: number | null;
    degraded?: string | null;
  } | null;
  churnWatch?: {
    atRiskToday: number | null;
    criticalToday: number | null;
    degraded?: string | null;
  } | null;
  source?: string;
}

export interface EarningsCardRegion {
  bonus: {
    periodStartMs: number;
    periodEndMs: number;
    qualifyingCents: number;
    bonusCents: number;
    tier: unknown;
  } | null;
  tierProgress: unknown;
  nextPayoutMs: number | null;
  mtdCents: number | null;
}

export interface HistoricalViewMonth {
  startMs: number;
  endMs: number;
  creditedCents: number;
  orderCount?: number;
  partial?: boolean;
}

export interface HistoricalViewRegion {
  months: HistoricalViewMonth[];
  period?: MyActivityScope;
  source?: string;
}

export interface LeaderboardRegionRow {
  repId: string;
  rank: number;
  creditedCents: number;
  orderCount: number;
  callCount: number;
  isSelf: boolean;
  displayName: string | null;
  role: string | null;
}

export interface LeaderboardRegion {
  rows: LeaderboardRegionRow[];
  self: LeaderboardRegionRow | null;
  total: number;
  period: MyActivityScope;
}

export interface MyActivityComposite {
  result: 'SUCCESS';
  projectId: string;
  repId: string;
  scope: MyActivityScope;
  regions: Partial<Record<MyActivityRegionId, RegionEnvelope<unknown>>>;
  degraded: Array<{ region: MyActivityRegionId; reason: string }>;
}

export interface MyActivityCompositeStreamFrame {
  repId: string;
  items: Array<{ source: string; event: unknown }>;
}

export type FetchImpl = typeof fetch;
export type TokenProvider = () => string | null | Promise<string | null>;

export interface EventSourceLike {
  addEventListener(type: string, listener: (evt: { data: string }) => void): void;
  close(): void;
  onerror: ((evt: unknown) => void) | null;
}

export type EventSourceFactory = (
  url: string,
  init?: { withCredentials?: boolean; headers?: Record<string, string> },
) => EventSourceLike;

export interface MyActivityCompositeClientOptions {
  baseUrl: string;
  getToken: TokenProvider;
  getCustomerId?: () => string | null | Promise<string | null>;
  fetchImpl?: FetchImpl;
  eventSourceFactory?: EventSourceFactory;
  defaultTimeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 20_000;

function normalizeBaseUrl(baseUrl: string): string {
  if (!baseUrl || typeof baseUrl !== 'string') throw new Error('BASE_URL_REQUIRED');
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
}

function kindForStatus(status: number): RuntimeApiErrorKind {
  if (status === 401) return 'AUTH_FAILED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 429) return 'RATE_LIMITED';
  if (status === 400) return 'VALIDATION_ERROR';
  if (status === 504) return 'TIMEOUT';
  if (status >= 500) return 'SERVER_ERROR';
  return 'SERVER_ERROR';
}

function extractErrorCode(body: unknown): string | undefined {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const c = (body as { errorCode?: unknown }).errorCode;
    if (typeof c === 'string') return c;
  }
  return undefined;
}

export interface MyActivityCompositeApi {
  fetchComposite(
    projectId: string,
    opts?: {
      regions?: MyActivityRegionId[];
      scope?: MyActivityScope;
      signal?: AbortSignal;
      timeoutMs?: number;
    },
  ): Promise<MyActivityComposite>;
  subscribeStream(
    projectId: string,
    onFrame: (frame: MyActivityCompositeStreamFrame) => void,
    onError?: (err: RuntimeApiError) => void,
  ): () => void;
}

export class MyActivityCompositeClient implements MyActivityCompositeApi {
  private readonly baseUrl: string;
  private readonly getToken: TokenProvider;
  private readonly getCustomerId: () => string | null | Promise<string | null>;
  private readonly fetchImpl: FetchImpl;
  private readonly eventSourceFactory: EventSourceFactory;
  private readonly defaultTimeoutMs: number;

  constructor(opts: MyActivityCompositeClientOptions) {
    this.baseUrl = normalizeBaseUrl(opts.baseUrl);
    this.getToken = opts.getToken;
    this.getCustomerId = opts.getCustomerId ?? (() => null);
    // W38: never the bare `fetch` (see default-fetch.ts: Illegal invocation).
    this.fetchImpl = opts.fetchImpl ?? defaultFetch;
    this.eventSourceFactory =
      opts.eventSourceFactory ??
      ((url, init) =>
        new (globalThis as unknown as { EventSource: new (url: string, init?: unknown) => EventSourceLike }).EventSource(
          url,
          init,
        ));
    this.defaultTimeoutMs = opts.defaultTimeoutMs ?? DEFAULT_TIMEOUT_MS;
  }

  async fetchComposite(
    projectId: string,
    opts: {
      regions?: MyActivityRegionId[];
      scope?: MyActivityScope;
      signal?: AbortSignal;
      timeoutMs?: number;
    } = {},
  ): Promise<MyActivityComposite> {
    if (!projectId || typeof projectId !== 'string') {
      throw new RuntimeApiError({
        kind: 'VALIDATION_ERROR',
        status: 0,
        message: 'projectId is required',
        errorCode: 'PROJECT_ID_REQUIRED',
      });
    }
    const scope = opts.scope ?? 'today';
    const regions = opts.regions && opts.regions.length > 0 ? opts.regions : undefined;
    const params = new URLSearchParams({ scope });
    if (regions) params.set('regions', regions.join(','));
    const url = `${this.baseUrl}/api/projects/${encodeURIComponent(projectId)}/my-activity?${params.toString()}`;
    const token = await this.getToken();
    const customerId = (await this.getCustomerId()) ?? projectId;
    const controller = new AbortController();
    const timeoutMs = opts.timeoutMs ?? this.defaultTimeoutMs;
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const abortForwarder = () => controller.abort();
    if (opts.signal) {
      if (opts.signal.aborted) {
        clearTimeout(timer);
        throw new RuntimeApiError({ kind: 'TIMEOUT', status: 0, message: 'aborted', errorCode: 'ABORTED' });
      }
      opts.signal.addEventListener('abort', abortForwarder, { once: true });
    }
    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(customerId ? { 'X-Customer-Id': customerId } : {}),
        },
        signal: controller.signal,
      });
    } catch (err) {
      clearTimeout(timer);
      if (opts.signal) opts.signal.removeEventListener('abort', abortForwarder);
      if (controller.signal.aborted) {
        throw new RuntimeApiError({ kind: 'TIMEOUT', status: 0, message: 'request aborted', errorCode: 'ABORTED' });
      }
      throw new RuntimeApiError({
        kind: 'NETWORK_ERROR',
        status: 0,
        message: err instanceof Error ? err.message : 'Network failure',
        errorCode: 'NETWORK_ERROR',
      });
    } finally {
      clearTimeout(timer);
      if (opts.signal) opts.signal.removeEventListener('abort', abortForwarder);
    }
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    if (!response.ok) {
      throw new RuntimeApiError({
        kind: kindForStatus(response.status),
        status: response.status,
        message: `My activity composite returned ${response.status}`,
        errorCode: extractErrorCode(body) || 'UNEXPECTED',
        body,
      });
    }
    if (!body || typeof body !== 'object' || (body as { result?: string }).result !== 'SUCCESS') {
      throw new RuntimeApiError({
        kind: 'SERVER_ERROR',
        status: response.status,
        message: 'Unexpected composite body shape',
        errorCode: 'UNEXPECTED_BODY',
        body,
      });
    }
    return body as MyActivityComposite;
  }

  subscribeStream(
    projectId: string,
    onFrame: (frame: MyActivityCompositeStreamFrame) => void,
    onError?: (err: RuntimeApiError) => void,
  ): () => void {
    if (!projectId || typeof projectId !== 'string') {
      throw new RuntimeApiError({ kind: 'VALIDATION_ERROR', status: 0, message: 'projectId required', errorCode: 'PROJECT_ID_REQUIRED' });
    }
    const url = `${this.baseUrl}/api/projects/${encodeURIComponent(projectId)}/my-activity/stream`;
    const source = this.eventSourceFactory(url, { withCredentials: false });
    source.addEventListener('update', (evt) => {
      try {
        const parsed = JSON.parse(evt.data) as MyActivityCompositeStreamFrame;
        onFrame(parsed);
      } catch {
        if (onError) onError(new RuntimeApiError({ kind: 'SERVER_ERROR', status: 0, message: 'Bad SSE frame', errorCode: 'BAD_SSE_FRAME' }));
      }
    });
    source.onerror = () => {
      if (onError) onError(new RuntimeApiError({ kind: 'NETWORK_ERROR', status: 0, message: 'SSE error', errorCode: 'SSE_ERROR' }));
    };
    return () => source.close();
  }
}

export function createMyActivityCompositeClient(
  opts: MyActivityCompositeClientOptions,
): MyActivityCompositeClient {
  return new MyActivityCompositeClient(opts);
}
